import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import { Grid3x3, Trash2, Loader2, Plus, Pencil, X, Save } from "lucide-react";

const EMPTY = {
  serie_profil: "",
  inaltime: "18",
  latime: "25",
  tip: "standard",
  culoare: "",
};

export default function SprosePage() {
  const [items, setItems] = useState([]);
  const [optiuni, setOptiuni] = useState({
    serii: [], tipuri: ["standard", "nestandard"], culori: [],
    latimi_sugerate: [25, 35, 45, 55, 65, 75], inaltimi_sugerate: [18, 16, 14, 12, 10],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lst, opt] = await Promise.all([apiGet("/sprose"), apiGet("/sprose/optiuni")]);
      setItems(lst.items || []);
      if (opt) setOptiuni(opt);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      serie_profil: form.serie_profil.trim(),
      inaltime: parseInt(form.inaltime, 10),
      latime: parseInt(form.latime, 10),
      tip: (form.tip || "standard").trim().toLowerCase(),
      culoare: form.culoare.trim(),
    };
    try {
      if (editId) {
        await apiPut(`/sprose/${editId}`, payload);
        toast.success("Configurare actualizata");
      } else {
        await apiPost("/sprose", payload);
        toast.success("Configurare adaugata");
      }
      resetForm();
      await load();
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      serie_profil: r.serie_profil || "",
      inaltime: String(r.inaltime ?? ""),
      latime: String(r.latime ?? ""),
      tip: r.tip || "standard",
      culoare: r.culoare || "",
    });
  };

  const remove = async (r) => {
    if (!window.confirm(`Stergeti sprosul ${r.inaltime}x${r.latime} (${r.tip})?`)) return;
    try {
      await apiDelete(`/sprose/${r.id}`);
      toast.success("Configurare stearsa");
      setItems((prev) => prev.filter((x) => x.id !== r.id));
      if (editId === r.id) resetForm();
    } catch (ex) {
      toast.error(ex.message);
    }
  };

  const inp = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500";
  const lbl = "block text-slate-600 text-xs mb-1 uppercase tracking-wide";

  return (
    <div className="space-y-6" data-testid="sprose-page">
      <div className="flex items-center gap-3">
        <Grid3x3 className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">Configurare sprose</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-3">
        Catalog profile spros: sectiune inaltime x latime, tip standard/nestandard si culoare. Folosit de motorul de calcul Prod-2.
      </p>

      <form
        onSubmit={submit}
        className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
        data-testid="sprose-form"
      >
        <div>
          <label className={lbl}>Serie profil</label>
          <input
            list="sprose-serii-list" value={form.serie_profil}
            onChange={(e) => setForm({ ...form, serie_profil: e.target.value })}
            data-testid="sprose-serie-input" className={inp} placeholder="Toate / Amel-71"
          />
          <datalist id="sprose-serii-list">
            {(optiuni.serii || []).map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <label className={lbl}>Inaltime (mm)</label>
          <input
            required type="number" min="1" list="sprose-inaltimi-list" value={form.inaltime}
            onChange={(e) => setForm({ ...form, inaltime: e.target.value })}
            data-testid="sprose-inaltime-input" className={inp} placeholder="18"
          />
          <datalist id="sprose-inaltimi-list">
            {(optiuni.inaltimi_sugerate || []).map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <label className={lbl}>Latime (mm)</label>
          <input
            required type="number" min="1" list="sprose-latimi-list" value={form.latime}
            onChange={(e) => setForm({ ...form, latime: e.target.value })}
            data-testid="sprose-latime-input" className={inp} placeholder="25"
          />
          <datalist id="sprose-latimi-list">
            {(optiuni.latimi_sugerate || []).map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div>
          <label className={lbl}>Tip</label>
          <select
            value={form.tip}
            onChange={(e) => setForm({ ...form, tip: e.target.value })}
            data-testid="sprose-tip-select" className={inp}
          >
            {(optiuni.tipuri || ["standard", "nestandard"]).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={lbl}>Culoare</label>
          <input
            list="sprose-culori-list" value={form.culoare}
            onChange={(e) => setForm({ ...form, culoare: e.target.value })}
            data-testid="sprose-culoare-input" className={inp} placeholder="ex. RAL / stejar"
          />
          <datalist id="sprose-culori-list">
            {(optiuni.culori || []).map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div className="flex gap-2">
          <button
            type="submit" disabled={saving}
            data-testid="sprose-save-btn"
            className="flex-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-medium px-4 py-2 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editId ? "Salveaza" : "Adauga"}
          </button>
          {editId && (
            <button
              type="button" onClick={resetForm}
              data-testid="sprose-cancel-btn"
              className="rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 px-3 py-2 text-sm transition-colors"
              title="Anuleaza editarea"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se incarca...
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="sprose-empty">
            Nicio configurare de sprose inca.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="sprose-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-600">
                <th className="px-4 py-3 font-medium">Serie</th>
                <th className="px-4 py-3 font-medium text-center">Sectiune (I x L)</th>
                <th className="px-4 py-3 font-medium">Tip</th>
                <th className="px-4 py-3 font-medium">Culoare</th>
                <th className="px-4 py-3 font-medium text-right">Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`sprose-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.serie_profil || "Toate"}</td>
                  <td className="px-4 py-3 text-center font-semibold text-amber-700">{r.inaltime} x {r.latime} mm</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">{r.tip}</span>
                  </td>
                  <td className="px-4 py-3">{r.culoare || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => startEdit(r)}
                        data-testid={`sprose-edit-${r.id}`}
                        className="text-slate-500 hover:text-amber-600 transition-colors" title="Editeaza"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`sprose-delete-${r.id}`}
                        className="text-red-500 hover:text-red-700 transition-colors" title="Sterge"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
